export type EnvEntry = {
    name: string;
    value: string;
};

/**
 * Parses a .env file string into key-value pairs.
 * Handles:
 * - KEY=VALUE
 * - KEY="VALUE" and KEY='VALUE' (quoted values, with quote stripping)
 * - Comments (lines starting with #)
 * - Empty lines
 * - Inline comments after unquoted values
 * - export KEY=VALUE prefix
 */
export function parseEnvFile(content: string): EnvEntry[] {
    const entries: EnvEntry[] = [];

    for (const rawLine of content.split(/\r?\n/)) {
        const line = rawLine.trim();

        // Skip empty lines and comments
        if (!line || line.startsWith("#")) {
            continue;
        }

        // Strip optional "export " prefix
        const stripped = line.startsWith("export ") ? line.slice(7).trim() : line;

        const eqIndex = stripped.indexOf("=");
        if (eqIndex === -1) {
            continue;
        }

        const name = stripped.slice(0, eqIndex).trim();
        let value = stripped.slice(eqIndex + 1).trim();

        // Validate name: only alphanumeric and underscores
        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
            continue;
        }

        // Strip surrounding quotes
        if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
            value = value.slice(1, -1);
        } else {
            // Remove inline comments for unquoted values
            const commentIndex = value.indexOf(" #");
            if (commentIndex !== -1) {
                value = value.slice(0, commentIndex).trim();
            }
        }

        entries.push({ name, value });
    }

    return entries;
}
